/**
 * Aplicación de escritorio (agest-desktop).
 *
 * La versión llega por el user agent de Electron, así que el TPV puede
 * compararla con la última release publicada en GitHub sin que la app de
 * escritorio tenga que exponernos nada más.
 */

const REPO = "asoft-sl/agest-desktop";
const LATEST_RELEASE_URL = `https://api.github.com/repos/${REPO}/releases/latest`;

// Marca que URL de la recarga forzada de Ajustes > Aplicación.
const FORCE_RELOAD_PARAM = "v";

// Chrome, Electron y Safari también aparecen en el user agent con su versión,
// pero no son la versión de la app de escritorio.
const NOT_APP_PRODUCTS = new Set([
  "Chrome",
  "Chromium",
  "HeadlessChrome",
  "Electron",
  "Safari",
  "Mozilla",
  "AppleWebKit",
  "KHTML",
]);

export function isDesktopApp() {
  return (
    typeof window !== "undefined" &&
    typeof window.electron?.printTicket === "function"
  );
}

/**
 * Versión de la app de escritorio leída del user agent. Electron añade el
 * nombre y la versión del package.json: "... Asoft TPV/1.0.0 Chrome/...
 * Electron/40.6.1". Devuelve null si no se reconoce, para no inventar.
 */
export function installedVersion() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

  for (const [, product, version] of ua.matchAll(
    /([^\s/]+)\/(\d+(?:\.\d+)+)/g,
  )) {
    if (!NOT_APP_PRODUCTS.has(product)) return version;
  }

  return null;
}

/**
 * Última release publicada en GitHub con su instalador de Windows. Lanza si
 * GitHub no responde (repo privado, sin red o límite de peticiones) o si
 * todavía no se ha publicado ninguna versión.
 */
export async function fetchLatestRelease() {
  const response = await fetch(LATEST_RELEASE_URL, {
    headers: { Accept: "application/vnd.github+json" },
  });

  if (response.status === 404) {
    const error = new Error("Todavía no hay ninguna versión publicada");
    error.code = "no-releases";
    throw error;
  }

  if (!response.ok) {
    const error = new Error(
      response.status === 403
        ? "GitHub no ha podido comprobar las versiones"
        : "No se ha podido comprobar la última versión",
    );
    error.code = "unreachable";
    throw error;
  }

  const release = await response.json();
  const installer = (release.assets || []).find((asset) =>
    asset.name.toLowerCase().endsWith(".exe"),
  );

  return {
    tag: release.tag_name || "",
    notes: release.body || "",
    publishedAt: release.published_at,
    installerUrl: installer?.browser_download_url || null,
    installerName: installer?.name || null,
    installerSize: installer?.size || null,
  };
}

/** Compara dos versiones tipo v1.2 o 1.2.0 sin depender de como se escriban. */
/**
 * Vacía la caché del navegador y recarga sin reutilizar nada de lo cacheado.
 *
 * El TPV de escritorio reutiliza la caché HTTP de Chromium, así que un
 * despliegue nuevo no se ve hasta que el documento se vuelve a pedir. Desde la
 * web no se puede tocar esa caché de disco (el preload solo expone la
 * impresión): lo que sí es accesible se borra —CacheStorage y service
 * workers— y la recarga va con una URL distinta, para que Chromium no
 * reutilice ni el HTML ni los assets, que además llevan el hash del build en
 * el nombre. La sesión se conserva.
 */
export async function hardReload() {
  try {
    if (typeof window !== "undefined" && "caches" in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Sin CacheStorage, o sin permiso, se sigue con la recarga forzada.
  }

  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration) => registration.unregister()),
      );
    }
  } catch {
    // Ídem: lo que importa es que la recarga no reutilice la caché.
  }

  const url = new URL(window.location.href);
  url.searchParams.set(FORCE_RELOAD_PARAM, Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Quita el parámetro que deja `hardReload` en la URL, para que no acabe en el
 * historial ni en los marcadores. No recarga: solo reescribe la dirección.
 */
export function clearForceReloadParam() {
  if (typeof window === "undefined") return;
  if (!window.location.search.includes(FORCE_RELOAD_PARAM)) return;

  const url = new URL(window.location.href);
  url.searchParams.delete(FORCE_RELOAD_PARAM);
  window.history.replaceState({}, "", url);
}

export function isNewerVersion(latest, installed) {
  if (!latest) return false;

  const parse = (value) =>
    String(value || "")
      .replace(/^v/i, "")
      .split(".")
      .map((part) => parseInt(part, 10) || 0);

  const [major, minor, patch] = parse(latest);
  const [currentMajor, currentMinor, currentPatch] = parse(installed);

  return (
    major > currentMajor ||
    (major === currentMajor && minor > currentMinor) ||
    (major === currentMajor &&
      minor === currentMinor &&
      patch > currentPatch)
  );
}

export function formatSize(bytes) {
  if (!bytes) return null;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}